import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu.js';
import { Icon } from '@/components/ui/icon.js';
import { cn } from '@/lib/utils/cn.js';
import { formatRelative } from '@/lib/datetime/format.js';
import { ANNOUNCEMENT_QUERY_KEYS, fetchAnnouncements, markSeen, readSeen, type Announcement } from './api.js';

/**
 * TopBar announcements bell. Badge = active announcements not yet opened this
 * session; opening the list marks them seen. Pinned items always list first.
 */
export function AnnouncementsBell() {
  const { t, i18n } = useTranslation('common');
  const [seen, setSeen] = useState<string[]>(() => readSeen());

  const { data } = useQuery({
    queryKey: ANNOUNCEMENT_QUERY_KEYS.active,
    queryFn: fetchAnnouncements,
    staleTime: 60_000,
  });

  const items = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return b.publishFrom.localeCompare(a.publishFrom);
      }),
    [data],
  );

  const unread = items.filter((a) => !seen.includes(a.id)).length;

  const onOpenChange = (open: boolean) => {
    if (open && items.length) {
      const next = items.map((a) => a.id);
      setSeen(next);
      markSeen(next);
    }
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('announcements.title')}
          className="relative grid h-11 w-11 place-items-center rounded-full text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-elevated)]/60 hover:text-[var(--text-primary)]"
        >
          <Icon name="bell" size={20} />
          {unread > 0 && (
            <span className="absolute right-2 top-2 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--accent-strong)] px-1 text-[10px] font-bold leading-none text-black">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70dvh] w-80 overflow-y-auto p-0">
        <div className="border-b border-[var(--border-subtle)] px-3 py-2.5 text-[13px] font-bold text-[var(--text-primary)]">
          {t('announcements.title')}
        </div>
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-[13px] text-[var(--text-muted)]">
            {t('announcements.empty')}
          </div>
        ) : (
          items.map((a) => <AnnouncementItem key={a.id} a={a} lang={i18n.language} />)
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AnnouncementItem({ a, lang }: { a: Announcement; lang: string }) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [a.body, expanded]);

  return (
    <div className="border-b border-[var(--border-subtle)] px-3 py-2.5 last:border-b-0">
      <div className="flex items-start gap-1.5">
        {a.pinned && <Icon name="star" size={12} className="mt-0.5 shrink-0 text-[var(--accent-strong)]" />}
        <span className="min-w-0 break-words text-[13px] font-semibold text-[var(--text-primary)]">{a.title}</span>
      </div>
      <p
        ref={bodyRef}
        className={cn(
          'mt-0.5 whitespace-pre-line break-words text-[12px] leading-snug text-[var(--text-secondary)]',
          !expanded && 'line-clamp-2',
        )}
      >
        {a.body}
      </p>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] font-semibold text-[var(--accent-strong)] hover:underline"
        >
          {expanded ? t('announcements.showLess') : t('announcements.showMore')}
        </button>
      )}
      <div className="mt-1 text-[11px] text-[var(--text-muted)]">{formatRelative(a.publishFrom, lang)}</div>
    </div>
  );
}
